import { useFormContext } from 'react-hook-form';
import { flatten } from '../../../../utils/flattenObject';
import { generateInitialValuesForInputs } from '../../../Applications/ApplicationForm/initialValuesGenerator';
import InputError from '../../Form/Error';
import { HookArray } from '../../HookForm/Form/Array';
import { SectionTitle } from '../SectionTitle';
import { ConditionalComponent } from './ConditionalComponent';
import { inputMap } from './inputmap';
import type { JsonInput, Section } from './types';

export interface JsonInputRendererProps {
  section: Section;
  formValues: any;
  currentStep?: number;
}
export const JsonInputRenderer = (props: JsonInputRendererProps) => {
  const { section, formValues: values, currentStep = 0 } = props;
  const { name: sectionName } = section;
  const currentSectionRegEx = new RegExp(`${sectionName}.[0-9.]*message`);
  const {
    getValues,
    formState: { errors },
  } = useFormContext();
  const flatErrors = flatten(errors);
  const currentSectionErrors = Object.keys(flatErrors).filter((key) => key.match(currentSectionRegEx));
  const { inputs, title } = section;
  const renderInputs = (inputs: JsonInput[], nameAppendString?: string, arrayId?: string) => {
    return inputs.map((input) => {
      const {
        type,
        props,
        gridColSpan = 'col-span-4',
        isArray,
        isDependent,
        arrayAddButtonLabel,
        arrayName,
        minArrayLength,
        maxArrayLength,
        dependentInputName = '',
        dependentInputValue,
        inputs: nestedInput,
      } = input;
      const { Component, defaultProps } = inputMap[type];
      const arrayFieldInfo = nameAppendString?.split('.') || [];
      const getDependentValue = () => {
        if (arrayFieldInfo.length) {
          const dependentValue = arrayFieldInfo.reduce<any>((acc, current) => {
            if (current && acc) {
              acc = acc[current];
            }
            return acc;
          }, values);
          if (dependentValue) {
            // TODO: need to verify for nested objected inside array
            return dependentValue[dependentInputName];
          } else {
            return undefined;
          }
        } else {
          return getValues(dependentInputName);
        }
      };
      /**
       * for array field to work
       *
       * array fields name will be changed to arrayfieldName.index.inputFieldName
       *
       *  */
      const updatedName = `${nameAppendString || ''}${props.name}${type === 'file' && arrayId ? `:${arrayId}` : ''}`;
      const updatedProps = {
        ...props,
        name: updatedName,
      } as any;

      /**
       * Array field input values check
       */
      const dependentValue = getDependentValue();

      const inputComponent = (
        <div className={`${gridColSpan}`} key={updatedName}>
          {Component && (
            <ConditionalComponent
              name={updatedName}
              isDependent={isDependent}
              dependentInputValue={dependentInputValue}
              dependentValue={dependentValue}
              getDependentValue={getDependentValue}
            >
              <Component {...updatedProps} {...defaultProps} currentStep={type === 'file' ? currentStep : undefined} />
            </ConditionalComponent>
          )}
          {nestedInput &&
            nestedInput.length > 0 &&
            (isArray && arrayName ? (
              <HookArray
                addButtonLabel={arrayAddButtonLabel}
                name={arrayName}
                minArrayLength={minArrayLength}
                defaultValues={generateInitialValuesForInputs(nestedInput)}
                maxArrayLength={maxArrayLength}
              >
                {(fieldName, removeButton, arrayId) => (
                  <div className='grid grid-cols-12 gap-4 w-full h-full content-start'>
                    {renderInputs(nestedInput, fieldName, arrayId)}
                    {removeButton}
                  </div>
                )}
              </HookArray>
            ) : (
              renderInputs(nestedInput)
            ))}
        </div>
      );

      return <>{inputComponent}</>;
    });
  };
  return (
    <>
      {title && <SectionTitle title={title} />}
      {renderInputs(inputs)}
      {currentSectionErrors?.length > 0 && (
        <div className='col-span-12 space-y-5'>
          {currentSectionErrors?.map((errorKey, index) => (
            <InputError key={index}>{`* ${flatErrors[errorKey as keyof typeof flatErrors]}`}</InputError>
          ))}
        </div>
      )}
    </>
  );
};
